import * as Y from 'yjs'
import L from 'leaflet'
import { WebsocketProvider } from 'y-websocket'
import { setupDrawing } from './mapController.js'
import {
  FeatureRegistry,
  featureToLayer,
  applyPresentation,
  updateLayerGeometry,
  geometryFromLayer,
  shapeToKind,
  computeMeasurement,
  DEFAULT_STYLE,
} from './features.js'
import { createPresence } from './presence.js'
import { createMeasureTool } from './measure.js'
import { escapeHtml } from '../lib/escape.js'

// Marks Yjs transactions originating from this client. Remote transactions
// (applied by the provider) carry a different origin, so the observer can tell
// "my own write echoing back" from "someone else's change".
const LOCAL_ORIGIN = 'local-edit'

function wsBaseUrl() {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${proto}//${location.host}/yjs`
}

function nowIso() {
  return new Date().toISOString()
}

export function createRoomSession({ roomId, identity, map, callbacks = {} }) {
  const ydoc = new Y.Doc()
  const provider = new WebsocketProvider(wsBaseUrl(), roomId, ydoc)
  const features = ydoc.getMap('features')
  const registry = new FeatureRegistry()

  // True only while we mutate Leaflet in response to a remote Yjs change. Geoman
  // can fire edit events during programmatic geometry updates; this flag makes
  // those events no-ops so they never write back into Yjs (the classic echo loop).
  let applyingRemote = false
  let selectedId = null
  let destroyed = false
  let drawing = false

  setupDrawing(map)

  const presence = createPresence({
    provider,
    map,
    identity,
    onPresenceChange: (list) => {
      callbacks.onPresence?.(list)
      refreshEditingHighlights()
    },
  })
  const measureTool = createMeasureTool(map, {
    onChange: (isActive) => callbacks.onMeasureChange?.(isActive),
  })

  // Transient marker dropped by the location search (not persisted to Yjs).
  let searchMarker = null
  let searchTimer = null
  // Visual snap indicator (see showSnap/hideSnap).
  let snapIndicator = null
  // Per-user element limit for this room (null = unlimited). Set from room
  // metadata via setMaxFeatures once it loads.
  let maxFeatures = null

  // ---- ownership ---------------------------------------------------------------
  // Features carry the creator's stable per-browser ownerId. Older features
  // (created before ownership existed) have no ownerId — fall back to matching
  // the display name so legacy rooms stay usable. Client-side enforcement only;
  // fine for a classroom, not real auth.
  function ownsFeature(f) {
    if (!f) return false
    if (f.ownerId) return f.ownerId === identity.id
    return f.author === identity.name
  }

  function countOwnFeatures() {
    let n = 0
    features.forEach((f) => {
      if (ownsFeature(f)) n++
    })
    return n
  }

  function notice(message) {
    callbacks.onNotice?.(message)
  }

  // ---- snapshots for the React UI -------------------------------------------
  function snapshotFeatures() {
    const arr = []
    features.forEach((f) => arr.push(f))
    arr.sort((a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''))
    return arr
  }

  function notifyFeatures() {
    if (destroyed) return
    callbacks.onFeaturesChange?.(
      snapshotFeatures().map((f) => ({
        id: f.id,
        kind: f.kind,
        label: f.label || '',
        author: f.author,
        authorColor: f.authorColor,
        measurement: f.measurement || null,
      })),
    )
  }

  function featureSummary(id, override) {
    const f = override || features.get(id)
    if (!f) return null
    return {
      id,
      kind: f.kind,
      label: f.label || '',
      style: { ...DEFAULT_STYLE, ...f.style },
      measurement: f.measurement || null,
      author: f.author,
      authorColor: f.authorColor,
      owned: ownsFeature(f),
    }
  }

  // ---- write path (local -> Yjs) --------------------------------------------
  function writeFeature(feature) {
    ydoc.transact(() => features.set(feature.id, feature), LOCAL_ORIGIN)
    notifyFeatures()
  }

  function select(id) {
    selectedId = id
    presence.setEditing(id) // broadcast which feature we have open
    callbacks.onSelect?.(id ? featureSummary(id) : null)
  }

  // Geoman drew a brand-new shape. Adopt its layer (it's already on the map),
  // recolor/label it, register it, and publish to Yjs.
  function handleCreate(e) {
    if (applyingRemote) return
    const layer = e.layer
    const kind = shapeToKind(e.shape)
    if (!kind) {
      layer.remove()
      return
    }
    // Per-room element limit (set by whoever created the room).
    if (maxFeatures != null && countOwnFeatures() >= maxFeatures) {
      layer.remove()
      notice(`Limit reached — this room allows ${maxFeatures} element${maxFeatures === 1 ? '' : 's'} per person.`)
      return
    }
    const { geometry, extra } = geometryFromLayer(layer, kind)
    const style = { ...DEFAULT_STYLE, color: identity.color, fillColor: identity.color }
    if (extra.radius != null) style.radius = extra.radius
    const feature = {
      id: crypto.randomUUID(),
      kind,
      geojson: geometry,
      style,
      label: '',
      measurement: computeMeasurement(kind, geometry),
      author: identity.name,
      authorColor: identity.color,
      ownerId: identity.id,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    }
    registry.set(feature.id, layer)
    applyPresentation(layer, feature)
    bindLayerEvents(layer)
    writeFeature(feature)
    select(feature.id)
  }

  // A layer's geometry changed locally (vertex edit, drag, resize).
  function handleEdit(layer) {
    if (applyingRemote) return
    const id = registry.idOf(layer)
    if (!id) return
    const existing = features.get(id)
    if (!existing) return
    // Non-owned layers are pmIgnore'd so this shouldn't fire for them — but if
    // it somehow does (legacy layers), revert the geometry instead of writing.
    if (!ownsFeature(existing)) {
      updateLayerGeometry(layer, existing)
      notice(`Only ${existing.author} can change that.`)
      return
    }
    const { geometry, extra } = geometryFromLayer(layer, existing.kind)
    const style = { ...existing.style }
    if (extra.radius != null) style.radius = extra.radius
    const updated = {
      ...existing,
      geojson: geometry,
      style,
      measurement: computeMeasurement(existing.kind, geometry),
      updatedAt: nowIso(),
    }
    applyPresentation(layer, updated)
    writeFeature(updated)
    if (selectedId === id) callbacks.onSelect?.(featureSummary(id, updated))
  }

  function handleRemove(e) {
    if (applyingRemote) return
    const id = registry.idOf(e.layer)
    if (!id) return
    const existing = features.get(id)
    // Shouldn't fire for pmIgnore'd layers, but if it does: put the layer back
    // on the map instead of deleting someone else's work.
    if (existing && !ownsFeature(existing)) {
      e.layer.addTo(map)
      notice(`Only ${existing.author} can delete that.`)
      return
    }
    registry.delete(id) // Geoman already pulled the layer off the map
    ydoc.transact(() => features.delete(id), LOCAL_ORIGIN)
    if (selectedId === id) select(null)
    notifyFeatures()
  }

  function bindLayerEvents(layer) {
    layer.on('pm:edit', () => handleEdit(layer))
    layer.on('pm:update', () => handleEdit(layer))
    layer.on('pm:markerdragend', () => {
      handleEdit(layer)
      hideSnap()
    })
    layer.on('pm:dragstart', () => {
      const id = registry.idOf(layer)
      if (id) presence.setEditing(id)
    })
    layer.on('pm:dragend', () => {
      handleEdit(layer)
      presence.setEditing(selectedId)
      hideSnap()
    })
    layer.on('click', () => {
      if (!measureTool.isActive()) select(registry.idOf(layer))
    })
    // Show the snap indicator while dragging this layer's vertices in edit mode.
    layer.on('pm:snap', (e) => showSnap(e.snapLatLng))
    layer.on('pm:unsnap', hideSnap)
  }

  // ---- snap indicator --------------------------------------------------------
  // Snapping (Geoman, snapDistance 30) works, but is otherwise invisible. Show a
  // pulsing ring at the snap point so users can SEE a vertex grab onto another.
  function snapIcon() {
    return L.divIcon({
      className: 'snap-indicator',
      html: '<div class="snap-ring"></div>',
      iconSize: [28, 28],
      iconAnchor: [14, 14],
    })
  }
  function showSnap(latlng) {
    if (!latlng) return
    if (!snapIndicator) {
      snapIndicator = L.marker(latlng, {
        icon: snapIcon(),
        interactive: false,
        keyboard: false,
        zIndexOffset: 12000,
        pmIgnore: true, // never a snap target / editable itself
        snapIgnore: true,
      }).addTo(map)
    } else {
      snapIndicator.setLatLng(latlng)
    }
  }
  function hideSnap() {
    if (snapIndicator) {
      snapIndicator.remove()
      snapIndicator = null
    }
  }

  // ---- read path (Yjs -> Leaflet) -------------------------------------------
  function observer(event, transaction) {
    if (destroyed) return
    const isLocal = transaction.origin === LOCAL_ORIGIN
    applyingRemote = true
    try {
      event.changes.keys.forEach((change, id) => {
        if (change.action === 'delete') {
          const layer = registry.get(id)
          if (layer) {
            layer.remove()
            registry.delete(id)
          }
          if (selectedId === id) select(null)
          return
        }
        // We already reflected our own create/edit on the map; skip the echo.
        if (isLocal) return

        const feature = features.get(id)
        if (!feature) return
        const existing = registry.get(id)
        if (!existing) {
          // Others' features are locked from Geoman (pmIgnore) so edit/drag/
          // delete modes skip them, but stay snappable (snapIgnore: false).
          const locked = !ownsFeature(feature)
          const layer = featureToLayer(
            feature,
            locked ? { pmIgnore: true, snapIgnore: false } : {},
          )
          if (!layer) return
          registry.set(id, layer)
          layer.addTo(map)
          applyPresentation(layer, feature)
          bindLayerEvents(layer)
        } else {
          updateLayerGeometry(existing, feature)
          applyPresentation(existing, feature)
          if (selectedId === id) callbacks.onSelect?.(featureSummary(id, feature))
        }
      })
    } finally {
      applyingRemote = false
    }
    notifyFeatures()
    refreshEditingHighlights()
  }

  // ---- "being edited by someone else" highlight -----------------------------
  function refreshEditingHighlights() {
    const editors = presence.getEditors()
    for (const id of registry.byId.keys()) {
      const layer = registry.get(id)
      const el = typeof layer.getElement === 'function' ? layer.getElement() : layer._path
      if (!el || !el.classList) continue
      if ((editors.get(id) || []).length) el.classList.add('being-edited')
      else el.classList.remove('being-edited')
    }
  }

  // ---- provider status -------------------------------------------------------
  function onStatus(e) {
    callbacks.onStatus?.(e.status)
  }
  provider.on('status', onStatus)

  // ---- wire up ---------------------------------------------------------------
  // Named handlers so destroy() can unregister them (anonymous handlers can't
  // be removed by reference, and would linger as stale closures on remount).
  function onDrawStart(e) {
    drawing = true
    // The working layer fires pm:snap/pm:unsnap as the cursor snaps while drawing.
    const wl = e && e.workingLayer
    if (wl) {
      wl.on('pm:snap', (ev) => showSnap(ev.snapLatLng))
      wl.on('pm:unsnap', hideSnap)
    }
  }
  function onDrawEnd() {
    drawing = false
    hideSnap()
  }
  function onMapClick() {
    // Click on empty map clears selection (unless drawing/measuring).
    if (!measureTool.isActive() && !drawing) select(null)
  }

  // Esc cancels whatever tool/mode is active (draw, edit, drag, remove, measure).
  function onKeyDown(e) {
    if (e.key !== 'Escape') return
    const pm = map.pm
    if (pm.globalDrawModeEnabled()) pm.disableDraw()
    if (pm.globalEditModeEnabled()) pm.disableGlobalEditMode()
    if (pm.globalDragModeEnabled()) pm.disableGlobalDragMode()
    if (pm.globalRemovalModeEnabled()) pm.disableGlobalRemovalMode()
    if (measureTool.isActive()) measureTool.setActive(false) // notifies onMeasureChange
  }

  features.observe(observer)
  map.on('pm:create', handleCreate)
  map.on('pm:remove', handleRemove)
  map.on('pm:drawstart', onDrawStart)
  map.on('pm:drawend', onDrawEnd)
  map.on('click', onMapClick)
  document.addEventListener('keydown', onKeyDown)

  // ---- location search -------------------------------------------------------
  function searchPinIcon() {
    return L.divIcon({
      className: 'search-pin',
      html:
        '<div class="search-pin-ring"></div><div class="search-pin-dot"></div>' +
        '<button type="button" class="search-pin-add" title="Drop a pin here" aria-label="Drop a pin here">' +
        '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">' +
        '<path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="currentColor"/>' +
        '<circle cx="12" cy="9" r="2.5" fill="#fff"/></svg>' +
        '</button>',
      iconSize: [22, 22],
      iconAnchor: [11, 11],
      tooltipAnchor: [0, -10],
    })
  }

  // Create a real (persisted, owned) marker feature at a point — used by the
  // search pin's "drop a pin here" button. Same limit check as drawing.
  function addMarkerFeature(lat, lng, label) {
    if (maxFeatures != null && countOwnFeatures() >= maxFeatures) {
      notice(`Limit reached — this room allows ${maxFeatures} element${maxFeatures === 1 ? '' : 's'} per person.`)
      return false
    }
    const feature = {
      id: crypto.randomUUID(),
      kind: 'marker',
      geojson: { type: 'Point', coordinates: [lng, lat] },
      style: { ...DEFAULT_STYLE, color: identity.color, fillColor: identity.color },
      label: label || '',
      measurement: null,
      author: identity.name,
      authorColor: identity.color,
      ownerId: identity.id,
      createdAt: nowIso(),
      updatedAt: nowIso(),
    }
    const layer = featureToLayer(feature)
    registry.set(feature.id, layer)
    layer.addTo(map)
    applyPresentation(layer, feature)
    bindLayerEvents(layer)
    writeFeature(feature)
    select(feature.id)
    return true
  }

  function clearSearchMarker() {
    if (searchTimer) {
      clearTimeout(searchTimer)
      searchTimer = null
    }
    if (searchMarker) {
      searchMarker.remove()
      searchMarker = null
    }
  }

  // Fly to a geocoded result and drop a transient (non-persisted) marker so the
  // user can see where the search landed before drawing there.
  function gotoLocation({ lat, lng, label }) {
    // Number.isFinite (not typeof) — typeof NaN === 'number' would slip through
    // and throw "Invalid LatLng (NaN, NaN)".
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
    clearSearchMarker()
    // setView, not flyTo: flyTo's smooth-zoom interpolation can produce NaN on
    // long jumps with a large zoom delta. setView animates safely.
    const z = map.getZoom()
    const targetZoom = Number.isFinite(z) ? Math.max(z, 15) : 15
    map.setView([lat, lng], targetZoom, { animate: true })
    searchMarker = L.marker([lat, lng], {
      icon: searchPinIcon(),
      interactive: false,
      keyboard: false,
      zIndexOffset: 9000,
    }).addTo(map)
    if (label) {
      searchMarker
        .bindTooltip(escapeHtml(label), {
          permanent: true,
          direction: 'top',
          className: 'search-tooltip',
        })
        .openTooltip()
    }
    // The waypoint button on the pin turns this transient result into a real
    // marker feature. Keep the label short — first address segment, capped to
    // the editor's 60-char max.
    const btn = searchMarker.getElement()?.querySelector('.search-pin-add')
    if (btn) {
      const shortLabel = (label || '').split(',')[0].trim().slice(0, 60)
      btn.addEventListener('click', (ev) => {
        ev.preventDefault()
        ev.stopPropagation()
        if (addMarkerFeature(lat, lng, shortLabel)) clearSearchMarker()
      })
    }
    searchTimer = setTimeout(clearSearchMarker, 20000)
  }

  // ---- public API ------------------------------------------------------------
  return {
    ydoc,
    provider,
    selectFeature: (id) => select(id),
    getSelected: () => (selectedId ? featureSummary(selectedId) : null),

    updateStyle(id, patch) {
      const existing = features.get(id)
      if (!existing) return
      if (!ownsFeature(existing)) {
        notice(`Only ${existing.author} can change that.`)
        return
      }
      const updated = { ...existing, style: { ...existing.style, ...patch }, updatedAt: nowIso() }
      const layer = registry.get(id)
      if (layer) applyPresentation(layer, updated)
      writeFeature(updated)
      if (selectedId === id) callbacks.onSelect?.(featureSummary(id, updated))
    },

    updateLabel(id, label) {
      const existing = features.get(id)
      if (!existing) return
      if (!ownsFeature(existing)) {
        notice(`Only ${existing.author} can change that.`)
        return
      }
      const updated = { ...existing, label, updatedAt: nowIso() }
      const layer = registry.get(id)
      if (layer) applyPresentation(layer, updated)
      writeFeature(updated)
      if (selectedId === id) callbacks.onSelect?.(featureSummary(id, updated))
    },

    deleteFeature(id) {
      const existing = features.get(id)
      if (existing && !ownsFeature(existing)) {
        notice(`Only ${existing.author} can delete that.`)
        return
      }
      const layer = registry.get(id)
      if (layer) {
        layer.remove()
        registry.delete(id)
      }
      ydoc.transact(() => features.delete(id), LOCAL_ORIGIN)
      if (selectedId === id) select(null)
      notifyFeatures()
    },

    // Per-user element limit (from room metadata, which loads async).
    setMaxFeatures(n) {
      maxFeatures = Number.isInteger(n) && n > 0 ? n : null
    },

    toggleMeasure: () => measureTool.toggle(),
    isMeasuring: () => measureTool.isActive(),

    gotoLocation,

    exportGeoJSON() {
      return {
        type: 'FeatureCollection',
        features: snapshotFeatures().map((f) => ({
          type: 'Feature',
          geometry: f.geojson,
          properties: {
            kind: f.kind,
            label: f.label || null,
            measurement: f.measurement || null,
            author: f.author,
            authorColor: f.authorColor || null,
          },
        })),
      }
    },

    fitToFeatures() {
      const layers = [...registry.values()]
      if (!layers.length) return false
      let bounds = null
      for (const layer of layers) {
        if (typeof layer.getBounds === 'function') {
          const b = layer.getBounds()
          bounds = bounds ? bounds.extend(b) : L.latLngBounds(b.getSouthWest(), b.getNorthEast())
        } else if (typeof layer.getLatLng === 'function') {
          const ll = layer.getLatLng()
          bounds = bounds ? bounds.extend(ll) : L.latLngBounds(ll, ll)
        }
      }
      if (bounds && bounds.isValid()) {
        map.fitBounds(bounds.pad(0.2))
        return true
      }
      return false
    },

    destroy() {
      destroyed = true
      clearSearchMarker()
      hideSnap()
      measureTool.destroy()
      presence.destroy()
      features.unobserve(observer)
      provider.off('status', onStatus)
      document.removeEventListener('keydown', onKeyDown)
      map.off('pm:create', handleCreate)
      map.off('pm:remove', handleRemove)
      map.off('pm:drawstart', onDrawStart)
      map.off('pm:drawend', onDrawEnd)
      map.off('click', onMapClick)
      // Drop per-layer Geoman/click listeners and release layer references.
      for (const layer of registry.values()) layer.off()
      registry.byId.clear()
      provider.destroy()
      ydoc.destroy()
    },
  }
}
